import { ScrollArea } from "@components/ui/scroll-area";
import type { TActionItem } from "@pages/analytics/types";
import { IconSparkles } from "@tabler/icons-react";
import { EmptyStateMessage } from "./empty-state-message";
import { ErrorStateMessage } from "./error-state-message";

interface ActionsTableProps {
  actions: TActionItem[];
  isLoading: boolean;
  isError: boolean;
}

const ActionRow = ({ item }: { item: TActionItem }) => {
  return (
    <div className="tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-py-3 tw-px-4 tw-border-solid last:tw-border-b-0 tw-min-w-0">
      <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-overflow-hidden">
        <IconSparkles className="tw-size-4 tw-text-blue-500 tw-flex-shrink-0" />
        {typeof item.slide_index === "number" && (
          <span className="tw-rounded-full tw-bg-blue-100 tw-px-1.5 tw-py-0.5 tw-font-sans tw-text-[10px] tw-font-medium tw-text-blue-500 tw-flex-shrink-0">
            Slide #{item.slide_index}
          </span>
        )}
        <span className="tw-font-sans tw-text-sm tw-font-medium tw-text-blue-800 tw-truncate tw-min-w-0">
          {item.event?.split("cta_")[1]}
        </span>
      </div>
      <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-text-right tw-flex-shrink-0">
        {item.actions}
      </span>
    </div>
  );
};

const Container = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="tw-rounded-lg tw-outline tw-outline-1 tw-py-5 tw-outline-blue/5 tw-bg-white tw-relative">
      <span className="tw-pb-4 tw-text-sm tw-font-sans tw-font-semibold tw-text-blue tw-block tw-px-5">
        Actions
      </span>
      {children}
    </div>
  );
};

const TableContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <ScrollArea className="tw-px-5 tw-h-[370px]">
      <div className="tw-min-w-0 tw-w-full">
        <div className="tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-py-3 tw-px-4 tw-border-solid">
          <span className="tw-font-sans tw-flex-1 tw-text-sm tw-text-blue-800 tw-min-w-0">
            Call to action
          </span>
          <span className="tw-font-sans tw-text-sm tw-text-blue-800 tw-text-right tw-flex-shrink-0">
            Clicks
          </span>
        </div>
        {children}
      </div>
    </ScrollArea>
  );
};

export const ActionsTable = ({
  actions,
  isLoading,
  isError,
}: ActionsTableProps) => {
  const hasActions = actions && actions.length > 0;

  if (isError) {
    return (
      <Container>
        <ErrorStateMessage />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <TableContainer>
          <ActionsTableSkeleton />
        </TableContainer>
      </Container>
    );
  }

  if (!hasActions) {
    return (
      <Container>
        <EmptyStateMessage
          title="No actions recorded"
          description="Call-to-action interactions will appear here once tracked."
          className="tw-max-w-64 tw-mx-auto tw-h-[370px]"
        />
      </Container>
    );
  }

  return (
    <Container>
      <TableContainer>
        {actions?.map((action, index) => (
          <ActionRow
            key={`${action.slide_index}-${action.event}-${index}`}
            item={action}
          />
        ))}
      </TableContainer>
    </Container>
  );
};

const ActionsTableSkeleton = () => {
  return (
    <div className="tw-px-5 tw-h-[370px]">
      {Array.from({ length: 7 }).map((_, index) => (
        <div
          key={index}
          className="tw-animate-skeleton-loading tw-flex tw-items-center tw-gap-6 tw-border-b tw-border-blue-100 tw-p-4 tw-border-solid"
        >
          <div className="tw-flex tw-items-center tw-gap-2 tw-flex-1 tw-min-w-0 tw-overflow-hidden">
            <span className="tw-rounded tw-bg-blue-100/50 tw-block tw-size-4" />
            <span className="tw-h-5 tw-w-14 tw-rounded-full tw-bg-blue-100/50 tw-block" />
            <span className="tw-h-1.5 tw-w-32 tw-rounded-lg tw-bg-blue-100/50 tw-block" />
          </div>
          <span className="tw-h-1.5 tw-rounded-lg tw-bg-blue-100/50 tw-block tw-w-4" />
        </div>
      ))}
    </div>
  );
};
